import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { UiKitModule } from '@serviceplan/ui-kit';
import { ClientCoreModule, ClientEnvironment, ENVIRONMENT } from '@thehubz/client/core';
import { Asset } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { firstValueFrom } from 'rxjs';
import { JackpotModalComponent } from '../jackpot-modal/jackpot-modal.component';

@Component({
  selector: 'wl-jackpot-download',
  standalone: true,
  imports: [CommonModule, UiKitModule, MatFormFieldModule, ReactiveFormsModule, ClientCoreModule],
  templateUrl: './jackpot-download.component.html',
  styleUrls: ['./jackpot-download.component.scss'],
})
export class JackpotDownloadComponent {
  downloadForm: UntypedFormGroup;
  checked = false;
  allComplete = true;
  assets: AnyType;
  info: AnyType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AnyType,
    @Inject(ENVIRONMENT) protected readonly env: ClientEnvironment,
    private _formBuilder: UntypedFormBuilder,
    private dialogRef: MatDialogRef<JackpotDownloadComponent>,
    private matDialog: MatDialog,
  ) {
    const enableTerms = this.env.termsConditionsPopup?.onAssetDownload ? false : true;
    this.downloadForm = this._formBuilder.group({
      isActive: [true],
      selected: [data.assets, [Validators.required]],
      expiryAndTerms: [enableTerms, [Validators.requiredTrue]],
    });
    this.assets = this.data.assets;
    this.info = this.data.info;
  }

  onSubmit() {
    const assetIDs = this.downloadForm.get('selected')?.value.map((asset: Asset) => asset._id);
    this.dialogRef.close({
      assetIDs: assetIDs,
    });
  }

  someComplete(): boolean {
    const selectedAssetFormValue = this.downloadForm.get('selected')?.value;
    if (selectedAssetFormValue === '') {
      return false;
    }
    return selectedAssetFormValue.length > 0 && !this.allComplete;
  }

  checkAll(completed: boolean) {
    this.allComplete = completed;
    if (this.allComplete) {
      this.downloadForm.get('selected')?.setValue(this.assets);
    } else {
      this.downloadForm.get('selected')?.setValue('');
    }
  }

  getCheckedStatus(assetId: AnyType) {
    const selectedAssets = this.downloadForm.get('selected')?.value;
    if (selectedAssets) {
      const selectedAsset = selectedAssets?.filter((asset: { _id: AnyType }) => {
        return asset._id === assetId;
      });
      if (selectedAsset.length) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  checkboxClick(assetId: AnyType) {
    const selectedAssetFormValue = this.downloadForm.get('selected')?.value;
    const selectedAssets = selectedAssetFormValue ? selectedAssetFormValue : [];
    const selectedAsset = selectedAssets?.filter((asset: { _id: AnyType }) => {
      return asset._id === assetId;
    });
    if (selectedAsset.length) {
      const filteredAssets = selectedAssets?.filter((asset: { _id: AnyType }) => {
        return asset._id !== assetId;
      });
      this.downloadForm.get('selected')?.setValue(filteredAssets);
    } else {
      const newAsset = this.assets.filter((asset: { _id: AnyType }) => {
        return asset._id === assetId;
      });
      selectedAssets.push(...newAsset);
      this.downloadForm.get('selected')?.setValue(selectedAssets);
    }
    this.allComplete = this.downloadForm.get('selected')?.value.length === this.assets.length;
  }

  checkboxTermsClick() {
    this.checked = !this.checked;
    this.downloadForm.get('expiryAndTerms')?.setValue(this.checked);
  }

  async openTerms(event: AnyType) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.matDialog.open(JackpotModalComponent, {
      minHeight: '40rem',
      maxWidth: '40rem',
      data: {
        type: 'terms',
        header: 'Nutzungsbedingungen',
        showFooter: true,
      },
    });
    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result.accepted) {
      this.downloadForm.get('expiryAndTerms')?.setValue(false);
    } else {
      this.checked = true;
      this.downloadForm.get('expiryAndTerms')?.setValue(true);
    }
  }
}
